import {
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult,
} from '@azure/msal-browser';
import { authConfig } from '@/config/authConfig';
import { authService } from '@/auth/services/AuthService';

export interface EmbeddedAuthTokenResult {
  accessToken: string;
  expiresAt: string;
  scopes: string[];
  account: {
    id: string;
    username: string;
    displayName: string;
  };
}

export type EmbeddedAuthErrorCode =
  | 'popupBlocked'
  | 'userCancelled'
  | 'interactionRequired'
  | 'networkError'
  | 'unknown';

export class EmbeddedAuthError extends Error {
  code: EmbeddedAuthErrorCode;

  retryable: boolean;

  popupBlocked: boolean;

  constructor(
    code: EmbeddedAuthErrorCode,
    message: string,
    retryable = true,
    popupBlocked = false,
  ) {
    super(message);
    this.code = code;
    this.retryable = retryable;
    this.popupBlocked = popupBlocked;
  }
}

function toEmbeddedAuthError(error: unknown): EmbeddedAuthError {
  const message =
    error instanceof Error ? error.message : 'Authentication failed.';
  const normalized = message.toLowerCase();

  if (
    normalized.includes('popup_window_error') ||
    normalized.includes('popup')
  ) {
    return new EmbeddedAuthError(
      'popupBlocked',
      'Popup was blocked. Please allow popups and retry.',
      true,
      true,
    );
  }

  if (
    normalized.includes('user_cancelled') ||
    normalized.includes('user cancelled') ||
    normalized.includes('cancel')
  ) {
    return new EmbeddedAuthError(
      'userCancelled',
      'Sign-in was cancelled. Please try again.',
      true,
    );
  }

  if (error instanceof InteractionRequiredAuthError) {
    return new EmbeddedAuthError(
      'interactionRequired',
      'Additional interaction is required to sign in.',
      true,
    );
  }

  if (normalized.includes('network')) {
    return new EmbeddedAuthError(
      'networkError',
      'A network error occurred during sign-in.',
      true,
    );
  }

  return new EmbeddedAuthError('unknown', message, true);
}

function mapResultToTokenResult(
  result: AuthenticationResult,
): EmbeddedAuthTokenResult {
  return {
    accessToken: result.accessToken,
    expiresAt: result.expiresOn?.toISOString() ?? new Date().toISOString(),
    scopes: result.scopes,
    account: {
      id: result.account?.homeAccountId ?? result.account?.localAccountId ?? '',
      username: result.account?.username ?? '',
      displayName: result.account?.name ?? result.account?.username ?? '',
    },
  };
}

export class MsalPopupAuthService {
  // Use the same MSAL instance as the main auth service to ensure
  // proper token cache sharing and avoid cross-instance issues.
  // This is critical for silent token acquisition to work correctly
  // when requesting tokens for different resources (e.g., On Demand API).
  private get msal(): PublicClientApplication {
    return authService.getMsalInstance();
  }

  private async ensureInitialized(): Promise<void> {
    // The main auth service handles initialization
    await authService.initialize();
  }

  private async ensureAccount(
    accountHint?: AccountInfo,
    interactive = true,
  ): Promise<AccountInfo> {
    if (accountHint) {
      this.msal.setActiveAccount(accountHint);
      return accountHint;
    }

    const active = this.msal.getActiveAccount();
    if (active) {
      return active;
    }

    const accounts = this.msal.getAllAccounts();

    if (accounts.length > 0) {
      this.msal.setActiveAccount(accounts[0]);
      return accounts[0];
    }

    // If no account is available and interactive mode is disabled,
    // throw an error instead of attempting to open a login popup.
    // This ensures silent authentication fails gracefully when no
    // cached account context is available.
    if (!interactive) {
      throw new EmbeddedAuthError(
        'interactionRequired',
        'No account available for silent authentication.',
        true,
      );
    }

    const loginResult = await this.msal.loginPopup({
      scopes: authConfig.scopes,
    });

    if (!loginResult.account) {
      throw new EmbeddedAuthError(
        'unknown',
        'Sign-in did not return an account context.',
        false,
      );
    }

    this.msal.setActiveAccount(loginResult.account);
    return loginResult.account;
  }

  async acquireToken(
    scopes: string[] = authConfig.scopes,
    accountHint?: AccountInfo,
    options: { interactive?: boolean } = {},
  ): Promise<EmbeddedAuthTokenResult> {
    const { interactive = true } = options;

    await this.ensureInitialized();

    try {
      const account = await this.ensureAccount(accountHint, interactive);

      try {
        const silentResult = await this.msal.acquireTokenSilent({
          scopes,
          account,
        });
        return mapResultToTokenResult(silentResult);
      } catch (error) {
        if (!(error instanceof InteractionRequiredAuthError)) {
          throw error;
        }

        if (!interactive) {
          throw error;
        }

        const popupResult = await this.msal.acquireTokenPopup({
          scopes,
          account,
        });

        if (popupResult.account) {
          this.msal.setActiveAccount(popupResult.account);
        }

        return mapResultToTokenResult(popupResult);
      }
    } catch (error) {
      throw toEmbeddedAuthError(error);
    }
  }
}
